import { formatGiftBalance } from '../../../../components/horasRegalo/giftBalance';
import OficinaVirtualDetalle from '../../../../components/OficinaVirtualDetalle';
import { Afiliacion } from '../../../../services/afiliadosService';
import React, { useEffect, useRef, useState } from 'react';
import { Banknote, ChevronDown, FileSpreadsheet, FileText, Gift, Mail, ReceiptText } from 'lucide-react';
import { ReservationReport } from '../../../../services/citasService.ts';
import { exportReservationReportExcel, exportReservationReportPdf } from '../../../../utils/reservationReportExports.ts';

const money = (value: string) => `Bs ${Number(value || 0).toFixed(2)}`;

const ReservationReportSummary: React.FC<{ report: ReservationReport; oficinaVirtual?: Afiliacion | null }> = ({ report, oficinaVirtual }) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [exporting, setExporting] = useState<'pdf' | 'excel' | null>(null);
    const [exportError, setExportError] = useState('');
    const menuRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const firstOptionRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        const closeOnOutsideClick = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) setMenuOpen(false);
        };
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && menuOpen) {
                setMenuOpen(false);
                triggerRef.current?.focus();
            }
        };
        document.addEventListener('mousedown', closeOnOutsideClick);
        document.addEventListener('keydown', closeOnEscape);
        return () => {
            document.removeEventListener('mousedown', closeOnOutsideClick);
            document.removeEventListener('keydown', closeOnEscape);
        };
    }, [menuOpen]);

    useEffect(() => {
        if (menuOpen) firstOptionRef.current?.focus();
    }, [menuOpen]);

    const moveMenuFocus = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const options = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') || []);
        if (!options.length) return;
        const current = options.indexOf(document.activeElement as HTMLButtonElement);
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            options[(current + 1) % options.length].focus();
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            options[(current - 1 + options.length) % options.length].focus();
        } else if (event.key === 'Home') {
            event.preventDefault();
            options[0].focus();
        } else if (event.key === 'End') {
            event.preventDefault();
            options[options.length - 1].focus();
        }
    };

    const downloadPdf = () => {
        setMenuOpen(false);
        triggerRef.current?.focus();
        setExportError('');
        setExporting('pdf');
        try {
            exportReservationReportPdf(report);
        } catch {
            setExportError('No se pudo generar el PDF. Intenta nuevamente.');
        } finally {
            setExporting(null);
        }
    };

    const downloadExcel = async () => {
        setMenuOpen(false);
        triggerRef.current?.focus();
        setExportError('');
        setExporting('excel');
        try {
            await exportReservationReportExcel(report);
        } catch {
            setExportError('No se pudo generar el Excel. Intenta nuevamente.');
        } finally {
            setExporting(null);
        }
    };

    return (
    <section aria-labelledby="reservation-report-title" style={{ display: 'grid', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div>
                <h2 id="reservation-report-title" style={{ margin: 0, fontSize: '1.15rem', fontWeight: 900, color: '#0f172a' }}>Reporte general de tu reserva</h2>
                <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '.8rem' }}>Reserva, pagos, deuda, horas de regalo y correspondencia.</p>
            </div>
            <div ref={menuRef} style={{ position: 'relative' }}>
                <button
                    ref={triggerRef}
                    type="button"
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    aria-disabled={exporting !== null}
                    onClick={() => {
                        if (exporting === null) setMenuOpen(open => !open);
                    }}
                    onKeyDown={event => {
                        if (exporting !== null) return;
                        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                            event.preventDefault();
                            setMenuOpen(true);
                        }
                    }}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 7, border: 0, borderRadius: 10, background: '#007FC8', color: '#fff', padding: '10px 14px', fontWeight: 800, cursor: exporting ? 'wait' : 'pointer' }}
                >
                    <ReceiptText size={15} />
                    {exporting ? 'Generando…' : 'Generar reporte'}
                    <ChevronDown size={14} aria-hidden="true" />
                </button>
                {menuOpen && (
                    <div role="menu" aria-label="Formatos del reporte" onKeyDown={moveMenuFocus} style={{ position: 'absolute', zIndex: 20, top: 'calc(100% + 7px)', right: 0, minWidth: 190, padding: 6, borderRadius: 12, border: '1px solid #e2e8f0', background: '#fff', boxShadow: '0 14px 35px rgba(15,23,42,.16)' }}>
                        <button ref={firstOptionRef} type="button" role="menuitem" onClick={downloadPdf} style={menuItemStyle}>
                            <FileText size={16} color="#FF2052" /> Descargar PDF
                        </button>
                        <button type="button" role="menuitem" onClick={() => void downloadExcel()} style={menuItemStyle}>
                            <FileSpreadsheet size={16} color="#00a88f" /> Descargar Excel
                        </button>
                    </div>
                )}
            </div>
        </div>
        {exportError && <div role="alert" style={{ padding: '10px 12px', borderRadius: 10, background: '#fff1f4', color: '#9B004A', fontSize: '.78rem', fontWeight: 800 }}>{exportError}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(145px, 1fr))', gap: 10 }}>
            {[
                ['Total', money(report.finanzas.total), '#FF2052'],
                ['Monto pagado', money(report.finanzas.pagado), '#00a88f'],
                ['Deuda', money(report.finanzas.deuda), '#EC8400'],
                ['Horas disponibles', formatGiftBalance(report.horas_regalo.disponibles_minutos), '#007FC8'],
            ].map(([label, value, color]) => (
                <div key={label} style={{ background: '#fff', border: '1.5px solid #f1f5f9', borderTop: `3px solid ${color}`, borderRadius: 14, padding: 14 }}>
                    <div style={{ color: '#94a3b8', fontSize: '.68rem', fontWeight: 800, textTransform: 'uppercase' }}>{label}</div>
                    <div style={{ color, fontSize: '1.08rem', fontWeight: 900, marginTop: 5 }}>{value}</div>
                </div>
            ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 12 }}>
            <article style={cardStyle}>
                <h3 style={titleStyle}><Banknote size={17} color="#00a88f" /> Pagos</h3>
                <p style={summaryStyle}>Estado: <strong>{report.finanzas.estado_pago}</strong></p>
                {report.finanzas.pagos.length === 0 ? <p style={emptyStyle}>Todavía no hay movimientos registrados.</p> : report.finanzas.pagos.map(payment => (
                    <div key={payment.id} style={rowStyle}><span>{payment.tipo_pago}</span><strong>{money(payment.monto)}</strong></div>
                ))}
            </article>
            <article style={cardStyle}>
                <h3 style={titleStyle}><Gift size={17} color="#007FC8" /> Horas de regalo</h3>
                <div style={rowStyle}><span>Asignadas</span><strong>{formatGiftBalance(Number(report.horas_regalo.asignadas) * 60)}</strong></div>
                <div style={rowStyle}><span>Usadas</span><strong>{formatGiftBalance(Number(report.horas_regalo.usadas) * 60)}</strong></div>
                <div style={rowStyle}><span>Disponibles</span><strong>{formatGiftBalance(report.horas_regalo.disponibles_minutos)}</strong></div>
            </article>
        </div>

        <article style={cardStyle}>
            <h3 style={titleStyle}><Mail size={17} color="#9B004A" /> Correspondencia ({report.correspondencia.total})</h3>
            {!report.correspondencia.habilitada ? <p style={emptyStyle}>Esta reserva no incluye recepción de correspondencia.</p> : report.correspondencia.items.length === 0 ? <p style={emptyStyle}>No tienes correspondencia registrada.</p> : report.correspondencia.items.map(item => (
                <div key={item.id} style={{ ...rowStyle, alignItems: 'flex-start' }}>
                    <div><strong style={{ color: '#0f172a' }}>{item.descripcion || 'Correspondencia sin descripción'}</strong><div style={{ color: '#94a3b8', fontSize: '.7rem', marginTop: 3 }}>{new Date(item.fecha_recepcion).toLocaleString('es-BO')}</div></div>
                    <span style={{ color: '#9B004A', background: '#fff1f4', borderRadius: 999, padding: '4px 8px', fontSize: '.68rem', fontWeight: 900, textTransform: 'capitalize' }}>{item.estado}</span>
                </div>
            ))}
        </article>
        {oficinaVirtual && <OficinaVirtualDetalle expediente={oficinaVirtual} />}
    </section>
    );
};

const menuItemStyle: React.CSSProperties = {
    width: '100%', display: 'flex', alignItems: 'center', gap: 9, border: 0,
    borderRadius: 8, background: '#fff', color: '#334155', padding: '10px 11px',
    fontSize: '.78rem', fontWeight: 800, cursor: 'pointer', textAlign: 'left',
};

const cardStyle: React.CSSProperties = { background: '#fff', border: '1.5px solid #f1f5f9', borderRadius: 16, padding: 16 };
const titleStyle: React.CSSProperties = { margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 7, color: '#0f172a', fontSize: '.9rem', fontWeight: 900 };
const rowStyle: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 0', borderTop: '1px solid #f1f5f9', color: '#64748b', fontSize: '.78rem' };
const summaryStyle: React.CSSProperties = { color: '#64748b', fontSize: '.8rem', margin: '0 0 8px' };
const emptyStyle: React.CSSProperties = { color: '#94a3b8', fontSize: '.78rem', margin: 0 };

export default ReservationReportSummary;

